import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useExpenseReportDaily } from '@/hooks/use-expense-report-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/reports/daily',
)({
    component: DailyReportPage,
})

function todayYMD(): string {
    return new Date().toISOString().slice(0, 10)
}

function DailyReportPage() {
    const [from, setFrom] = useState(todayYMD())
    const [to, setTo] = useState(todayYMD())
    const [counterparty, setCounterparty] = useState('')
    const [paymentMethod, setPaymentMethod] = useState<string>('')

    const { data, isLoading } = useExpenseReportDaily({
        from,
        to,
        counterparty: counterparty || undefined,
        payment_method:
            (paymentMethod as 'cash' | 'bank_transfer') || undefined,
    })

    const entries = data?.entries ?? []
    const summary = data?.summary

    return (
        <div className="space-y-4 p-4">
            <h1 className="text-xl font-semibold">
                {m.expense_report_daily()}
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <div>
                    <Label>{m.expense_filter_from()}</Label>
                    <Input
                        type="date"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                    />
                </div>
                <div>
                    <Label>{m.expense_filter_to()}</Label>
                    <Input
                        type="date"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                    />
                </div>
                <div>
                    <Label>{m.expense_vendor_name()}</Label>
                    <Input
                        placeholder="..."
                        value={counterparty}
                        onChange={(e) => setCounterparty(e.target.value)}
                    />
                </div>
                <div>
                    <Label>{m.expense_payment_method()}</Label>
                    <Select
                        value={paymentMethod || 'all'}
                        onValueChange={(v) =>
                            setPaymentMethod(v === 'all' ? '' : v)
                        }
                    >
                        <SelectTrigger>
                            <SelectValue
                                placeholder={m.expense_filter_all_methods()}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">
                                {m.expense_filter_all_methods()}
                            </SelectItem>
                            <SelectItem value="cash">
                                {m.expense_payment_method_cash()}
                            </SelectItem>
                            <SelectItem value="bank_transfer">
                                {m.expense_payment_method_bank_transfer()}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 print:hidden">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">
                            {m.expense_report_total_income()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-emerald-600">
                            {formatVnd(summary?.total_income ?? 0)}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">
                            {m.expense_report_total_expense()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-rose-600">
                            {formatVnd(summary?.total_expense ?? 0)}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">
                            {m.expense_report_net_cash_flow()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p
                            className={
                                'text-2xl font-bold ' +
                                ((summary?.net_cash_flow ?? 0) >= 0
                                    ? 'text-emerald-600'
                                    : 'text-rose-600')
                            }
                        >
                            {formatVnd(summary?.net_cash_flow ?? 0)}
                        </p>
                    </CardContent>
                </Card>
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.expense_daily_col_code()}</TableHead>
                        <TableHead>{m.expense_daily_col_type()}</TableHead>
                        <TableHead>{m.expense_vendor_name()}</TableHead>
                        <TableHead className="text-right">
                            {m.expense_amount()}
                        </TableHead>
                        <TableHead>{m.expense_payment_method()}</TableHead>
                        <TableHead>{m.expense_payment_status()}</TableHead>
                        <TableHead>{m.expense_date()}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {!isLoading && entries.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={7}
                                className="text-center text-muted-foreground py-8"
                            >
                                {m.expense_report_no_data()}
                            </TableCell>
                        </TableRow>
                    )}
                    {entries.map((entry) => (
                        <TableRow key={entry.code}>
                            <TableCell className="font-mono text-xs">
                                {entry.code}
                            </TableCell>
                            <TableCell>
                                <Badge
                                    variant={
                                        entry.kind === 'income'
                                            ? 'default'
                                            : 'destructive'
                                    }
                                >
                                    {entry.kind === 'income'
                                        ? m.expense_kind_income()
                                        : m.expense_kind_expense()}
                                </Badge>
                            </TableCell>
                            <TableCell>{entry.counterparty}</TableCell>
                            <TableCell
                                className={
                                    'text-right font-mono ' +
                                    (entry.kind === 'income'
                                        ? 'text-emerald-600'
                                        : 'text-rose-600')
                                }
                            >
                                {entry.kind === 'income' ? '+' : '−'}
                                {formatVnd(entry.amount)}
                            </TableCell>
                            <TableCell>{entry.payment_method}</TableCell>
                            <TableCell>{entry.payment_status}</TableCell>
                            <TableCell>
                                {entry.occurred_at.slice(0, 10)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    )
}
